import type { LocalDatabaseCustomer } from "../../_Generated/Sync.gen";
import Cloner from "../../Utilities/Generic/Cloner";
import { customerGroupFragment } from "../CustomerGroups/CustomerGroupTypes";

export interface Customer {
    id?: string;
    firstName: string;
    lastName: string;
    code: string;
    company: string;
    businessNumber: string;
    birthday: string;
    gender: string;
    emails: Array<string>;
    fax: string;
    phone: string;
    mobile: string;
    allowMarketingEmails: boolean | null;
    website: string;
    twitter: string;
    facebook: string;
    billing: {
        addressStreet1: string;
        addressStreet2: string;
        addressSuburb: string;
        addressCity: string;
        addressPostcode: string;
        addressRegion: string;
        addressCountry: string;
        addressState?: string;
    };
    delivery: {
        addressStreet1: string;
        addressStreet2: string;
        addressSuburb: string;
        addressCity: string;
        addressPostcode: string;
        addressRegion: string;
        addressCountry: string;
        addressState?: string;
    };
    invoiceMessage: string;
    comments: string;
    customerGroup: null | string;
    loyaltyEnabled: boolean;
    loyaltyRate: null | number;
    loyaltyPoints: number;
    disablePromotions: boolean;
    priceList: null | string;
    accountLimit: null | number;
    currentOwing: number;
    requireOrderReference: boolean;
    overrideCustomerGroup: boolean;
}

export const customerFragment = `
    id,
    firstName,
    lastName,
    code,
    company,
    businessNumber,
    birthday,
    gender,
    emails,
    fax,
    phone,
    mobile,
    allowMarketingEmails,
    website,
    twitter,
    facebook,
    billing {
        id,
        addressStreet1,
        addressStreet2,
        addressSuburb,
        addressCity,
        addressPostcode,
        addressRegion,
        addressCountry,
        phone,
        mobile,
    },
    delivery {
        id,
        addressStreet1,
        addressStreet2,
        addressSuburb,
        addressCity,
        addressPostcode,
        addressRegion,
        addressCountry,
    },
    invoiceMessage,
    comments,
    customerGroup {
        ${customerGroupFragment}
    }
    loyaltyEnabled,
    loyaltyRate,
    loyaltyPoints,
    disablePromotions,
    priceList {
        id,
    },
    accountLimit,
    currentOwing,
    requireOrderReference,
    overrideCustomerGroup,
`;

export function normaliseCustomer(customer: any): Customer {
    const newCustomer = Cloner.Clone(customer);

    if(newCustomer.customerGroup && newCustomer.customerGroup.id) {
        newCustomer.customerGroup = newCustomer.customerGroup.id;
    }

    if(newCustomer.priceList && newCustomer.priceList.id) {
        newCustomer.priceList = newCustomer.priceList.id;
    }

    if(newCustomer.billing) {
        const { id, mobile, phone, ...newBilling } = newCustomer.billing;

        newCustomer.billing = newBilling;
    }

    if(newCustomer.delivery) {
        const { id, ...newDelivery } = newCustomer.delivery;

        newCustomer.delivery = newDelivery;
    }

    if(!newCustomer.customerGroup) {
        newCustomer.overrideCustomerGroup = false;
    }

    return {
        ...newCustomer,
    };
}

function getCustomerGroupValues(customer: any): Pick<LocalDatabaseCustomer,
    "account_limit" |
    "pricelist_id" |
    "loyalty" |
    "loyalty_rate" |
    "disable_promotions" |
    "allow_account_sales" |
    "autoprint_account_receipt" |
    "autoprint_receipt" |
    "autoprint_parked_receipt" |
    "require_order_reference"
> {
    if(customer.customerGroup) {
        if(customer.overrideCustomerGroup) {
            return {
                account_limit            : customer.accountLimit,
                pricelist_id             : customer.priceList ? customer.priceList.id : null,
                loyalty                  : customer.loyaltyEnabled,
                loyalty_rate             : customer.loyaltyRate,
                disable_promotions       : customer.disablePromotions,
                allow_account_sales      : customer.customerGroup.allowAccountSales,
                autoprint_account_receipt: customer.customerGroup.autoPrintPaymentReceipt,
                autoprint_receipt        : customer.customerGroup.autoPrintReceipt,
                autoprint_parked_receipt : customer.customerGroup.autoPrintParkedReceipt,
                require_order_reference  : customer.requireOrderReference,
            };
        } else {
            return {
                account_limit: customer.customerGroup.accountLimit,
                pricelist_id : customer.customerGroup.priceList ?
                    customer.customerGroup.priceList.id :
                    null,
                loyalty                  : customer.customerGroup.loyaltyEnabled,
                loyalty_rate             : customer.customerGroup.loyaltyRate,
                disable_promotions       : customer.customerGroup.disablePromotions,
                allow_account_sales      : customer.customerGroup.allowAccountSales,
                autoprint_account_receipt: customer.customerGroup.autoPrintPaymentReceipt,
                autoprint_receipt        : customer.customerGroup.autoPrintReceipt,
                autoprint_parked_receipt : customer.customerGroup.autoPrintParkedReceipt,
                require_order_reference  : customer.customerGroup.requireOrderReference,
            };
        }
    }

    return {
        account_limit            : customer.accountLimit,
        pricelist_id             : customer.priceList ? customer.priceList.id : null,
        loyalty                  : true,
        loyalty_rate             : customer.loyaltyRate,
        disable_promotions       : customer.disablePromotions,
        allow_account_sales      : false,
        autoprint_account_receipt: false,
        autoprint_receipt        : false,
        autoprint_parked_receipt : false,
        require_order_reference  : customer.requireOrderReference,
    };
}

export type LocalCustomer = LocalDatabaseCustomer;

export function transformCustomerToIndexedDB(customer: any): LocalCustomer {
    const fullName = customer.firstName + " " + customer.lastName;

    const customerGroupValues = getCustomerGroupValues(customer);

    return {
        account_receipt: (
            customer.customerGroup &&
            customer.customerGroup.paymentReceipt
        ) ?
            customer.customerGroup.paymentReceipt.id :
            null,
        billing: {
            uuid            : "",
            address_street_1: customer.billing.addressStreet1,
            address_street_2: customer.billing.addressStreet2,
            address_suburb  : customer.billing.addressSuburb,
            address_city    : customer.billing.addressCity,
            address_postcode: customer.billing.addressPostcode,
            address_state   : customer.billing.addressState,
            address_country : customer.billing.addressCountry,
            fax             : customer.fax,
            phone           : customer.phone,
            mobile          : customer.mobile,
            website         : customer.website,
            twitter         : customer.twitter,
            facebook        : customer.facebook,
            email           : customer.emails.join(";"),
        },
        billing_contact_id: customer.billing.id,
        birthday          : customer.birthday,
        business_number   : customer.businessNumber,
        client_id         : customer.clientId,
        code              : customer.code,
        comments          : customer.comments,
        company           : customer.company,
        current_owing     : customer.currentOwing,
        customer_group_id : customer.customerGroup ? customer.customerGroup.id : null,
        delivery          : {
            uuid            : "",
            address_street_1: customer.delivery.addressStreet1,
            address_street_2: customer.delivery.addressStreet2,
            address_suburb  : customer.delivery.addressSuburb,
            address_city    : customer.delivery.addressCity,
            address_postcode: customer.delivery.addressPostcode,
            address_state   : customer.billing.addressState,
            address_country : customer.delivery.addressCountry,
            fax             : "",
            phone           : "",
            mobile          : "",
            website         : "",
            twitter         : "",
            facebook        : "",
            email           : "",
        },
        delivery_contact_id    : customer.delivery.id,
        email                  : customer.emails.join(";"),
        first_name             : customer.firstName,
        full_name              : fullName,
        gender                 : customer.gender,
        invoice_message        : customer.invoiceMessage,
        last_name              : customer.lastName,
        loyalty_points         : customer.loyaltyPoints,
        override_customer_group: customer.overrideCustomerGroup,
        receipt                : (
            customer.customerGroup &&
            customer.customerGroup.defaultReceipt
        ) ?
            customer.customerGroup.defaultReceipt.id :
            null,
        phones: [
            customer.billing.phone,
            customer.billing.mobile,
        ],
        uuid       : customer.id,
        merged_into: null,
        name_index : [],
        ...customerGroupValues,
    };
}
